import * as React from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import backgroundImage from '../../../assets/images/stations.svg'; // use this to remove svg to jsxc errors: https://www.svgminify.com/
import { Button, Container, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const ProductHeroLayout = styled('section')(({ theme }) => ({
    color: theme.palette.common.white,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    overflow: 'hidden',
    [theme.breakpoints.up('sm')]: {
        minHeight: '100vh'
    }
}));

const Background = styled(Box)(({ theme }) => {
    const isDarkMode = theme.palette.mode === 'dark';
    const gradient = isDarkMode
        ? `linear-gradient(135deg, ${theme.palette.primary.lighter} 0%, ${theme.palette.primary[200]} 50%, ${theme.palette.primary.light} 100%)`
        : 'linear-gradient(135deg, #2e7d32 0%, #4caf50 50%, #66bb6a 100%)';

    return {
        color: theme.palette.common.white,
        background: gradient,
        position: 'absolute',
        backgroundRepeat: 'no-repeat',
        left: '-40px',
        right: 0,
        top: '3px',
        bottom: 0,
        backgroundSize: 'cover',
        zIndex: -1,
        [theme.breakpoints.up('lg')]: {
            minHeight: 700,
            background: gradient
        },
        [theme.breakpoints.up('xl')]: {
            minHeight: 900,
            background: gradient
        }
    };
});

const BoxImage = styled(Box)(({}) => ({
    alt: 'stations',
    width: '100%'
}));

const BoxImageSmall = styled(BoxImage)(({ theme }) => ({
    [theme.breakpoints.up('md')]: {
        display: 'none'
    }
}));

const BoxImageLarge = styled(BoxImage)(({ theme }) => ({
    display: 'none',
    [theme.breakpoints.up('md')]: {
        display: 'flex',
        position: 'absolute',
        left: '0px',
        top: '80px',
        width: '900px',
        height: 'auto',
        zIndex: 0
    },
    [theme.breakpoints.up('lg')]: {
        display: 'flex',
        width: '900px'
    },
    [theme.breakpoints.up('xl')]: {
        display: 'flex',
        width: '1000px',
        top: '60px'
    }
}));

const ListBox = styled(Box)(({ theme }) => ({
    top: '-20px',
    paddingTop: '20px',
    paddingBottom: '20px',
    paddingLeft: '20px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    [theme.breakpoints.up('md')]: {
        padding: 0,
        position: 'absolute',
        top: '-10%',
        left: '65%'
    },
    [theme.breakpoints.up('lg')]: {
        top: 0,
        left: '55%'
    },
    [theme.breakpoints.up('xl')]: {
        top: 0,
        left: '65%'
    }
}));

const Superscript = styled(Typography)(({}) => ({
    fontSize: '0.7rem',
    borderBottom: 'none',
    padding: 0,
    verticalAlign: 'top'
}));

const ListTableCellRight = styled(TableCell)(({ theme }) => ({
    display: 'flex',
    fontSize: '1.5rem',
    color: theme.palette.common.white,
    borderBottom: 'none',
    padding: 0,
    verticalAlign: 'center',
    [theme.breakpoints.up('lg')]: {
        fontSize: '2rem'
    }
}));

const Title = styled(Box)(({ theme }) => ({
    fontSize: '2rem',
    color: theme.palette.common.white,
    borderBottom: 'none',
    padding: 0,
    width: '100%',
    justifyContent: 'center',
    [theme.breakpoints.up('lg')]: {
        fontSize: '2.5rem'
    }
}));

function ProductHero() {
    return (
        <ProductHeroLayout>
            <Background />
            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, textAlign: 'center', py: 8 }}>
                <Box sx={{ maxWidth: '900px', margin: '0 auto' }}>
                    <Typography
                        variant="h1"
                        component="h1"
                        sx={{
                            fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem', lg: '5rem' },
                            fontWeight: 'bold',
                            mb: 3,
                            lineHeight: 1.2
                        }}
                    >
                        Workflow Orchestation
                        <br />
                        At Scale.
                    </Typography>

                    <Typography
                        variant="h5"
                        component="p"
                        sx={{
                            fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
                            mb: 5,
                            opacity: 0.95,
                            maxWidth: '700px',
                            margin: '0 auto 3rem'
                        }}
                    >
                        A developer-first workflow engine simple enough for AI to build with. Integrate alongside existing systems, migrate
                        at your own pace, and ship exactly what you need.
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            size="large"
                            href="http://stations.build/register/"
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                backgroundColor: 'white',
                                color: 'primary.main',
                                px: 4,
                                py: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                                }
                            }}
                        >
                            Get Started Free
                        </Button>
                        <Button
                            variant="outlined"
                            size="large"
                            href="https://docs.stations.build"
                            sx={{
                                borderColor: 'white',
                                color: 'white',
                                px: 4,
                                py: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                '&:hover': {
                                    borderColor: 'white',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                }
                            }}
                        >
                            View Documentation
                        </Button>
                    </Box>

                    <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                API-First
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                Build Your Way
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                No Lock-In
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                Your Code, Your Data
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                Fair Pricing
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                Pay For What You Use
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Container>
        </ProductHeroLayout>
    );
}

export default ProductHero;
